import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { getAlienColor } from '../utils/colors';

export type AlienType = 'top' | 'middle' | 'bottom';

/**
 * Alien: Individual alien entity in the invasion grid.
 */
export class Alien {
  private sprite: Phaser.Physics.Arcade.Sprite;
  private glowSprite: Phaser.GameObjects.Image;
  private alienType: AlienType;
  private points: number;
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, x: number, y: number, type: AlienType) {
    this.scene = scene;
    this.alienType = type;

    const textureKey = `alien-${type}`;

    // Create glow sprite behind the alien (larger, tinted, semi-transparent)
    this.glowSprite = scene.add.image(x, y, textureKey);
    this.glowSprite.setScale(1.5);
    this.glowSprite.setAlpha(0.25);
    this.glowSprite.setTint(getAlienColor(type));
    this.glowSprite.setBlendMode(Phaser.BlendModes.ADD);
    this.glowSprite.setDepth(4);

    // Add subtle pulsing glow animation
    scene.tweens.add({
      targets: this.glowSprite,
      alpha: { from: 0.2, to: 0.4 },
      scale: { from: 1.4, to: 1.6 },
      duration: 800 + Math.random() * 400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Create the main sprite
    this.sprite = scene.physics.add.sprite(x, y, textureKey);
    this.sprite.setDepth(5);

    // Assign points based on type
    switch (type) {
      case 'top':
        this.points = GAME_CONFIG.scoring.alienPoints.top;
        break;
      case 'middle':
        this.points = GAME_CONFIG.scoring.alienPoints.middle;
        break;
      case 'bottom':
        this.points = GAME_CONFIG.scoring.alienPoints.bottom;
        break;
    }
  }

  getSprite(): Phaser.Physics.Arcade.Sprite {
    return this.sprite;
  }

  getGlowSprite(): Phaser.GameObjects.Image {
    return this.glowSprite;
  }

  getType(): AlienType {
    return this.alienType;
  }

  getPoints(): number {
    return this.points;
  }

  destroy(): void {
    if (this.glowSprite) {
      this.scene.tweens.killTweensOf(this.glowSprite);
      this.glowSprite.destroy();
    }
    this.sprite.destroy();
  }
}
